/**
 * NMS Top Deals Component
 * Collection tabs + lazy product loading for the Top Deals carousel.
 *
 * - First tab's collection is server-rendered.
 * - Other collections are fetched on demand via the Section Rendering API
 *   ({collection_url}?section_id={items_section}) and cached in memory.
 * - When the section approaches the viewport (IntersectionObserver), remaining
 *   collections are prefetched in the background (max 2 concurrent) into the
 *   same cache - no DOM insertion, invisible to the customer.
 * - Product card HTML always comes from Liquid (nms-top-deals-products
 *   snippet -> nms-product-card); JS only handles tabs, fetching, DOM
 *   replacement, caching and carousel refresh.
 * - The scroll-carousel element itself is never replaced - its children are
 *   swapped so carousel prev/next controls and the scroll-progress component
 *   keep their bound listeners.
 *
 * Follows NMS component instance architecture (extends NMSBaseClass).
 * Scoped per section instance - multiple Top Deals sections don't interfere.
 */

const PREFETCH_MAX_CONCURRENT = 2;
const PREFETCH_ROOT_MARGIN = '300px 0px';

class NMSTopDeals extends window.NMSBaseClass {
  constructor(element) {
    super();

    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;
    this.progressId = element.dataset.progressId || this.sectionId;
    this.itemsSection = element.dataset.itemsSection || 'nms-top-deals-items';
    this.componentId = `top-deals-${this.sectionId}`;

    this.carousel = null;
    this.collectionCache = new Map(); // key -> Array<Element> (deal item nodes)
    this.pendingRequests = new Map(); // key -> { promise, controller, userInitiated }
    this.activeUserRequest = null; // { key, controller, userInitiated }
    this.activeChip = null; // currently selected chip
    this.loadedChip = null; // chip whose content is actually displayed
    this.activeKey = null; // collection key the UI is switching to

    this.prefetchQueue = []; // [{ key, url }] waiting for a prefetch slot
    this.prefetching = new Set(); // keys currently being prefetched
    this.prefetchStarted = false;
    this.prefetchObserver = null;

    window.neemans = window.neemans || {};
    window.neemans.topDeals = window.neemans.topDeals || {};
    window.neemans.topDeals[this.sectionId] = this;
  }

  init() {
    // Base init handles [ref] and [data-nms-click] auto-binding within this.element
    super.init();

    this.carousel = this.element.querySelector('scroll-carousel');
    this.activeChip = this.refs.collectionChips?.[0] || null;
    this.loadedChip = this.activeChip;
    this.activeKey = this.#keyForChip(this.activeChip);

    // Cache the server-rendered collection - switching back must not refetch
    if (this.carousel && this.activeKey) {
      const initialItems = Array.from(this.carousel.querySelectorAll('[data-deal-item]'));
      if (initialItems.length > 0) {
        this.collectionCache.set(this.activeKey, initialItems);
      }
    }

    this.#setupKeyboardNavigation();
    this.#setupPrefetch();
  }

  destroy() {
    if (this.prefetchObserver) {
      this.prefetchObserver.disconnect();
      this.prefetchObserver = null;
    }

    // Abort user-initiated requests; let prefetch requests die quietly too
    for (const entry of this.pendingRequests.values()) {
      entry.controller.abort();
    }
    this.pendingRequests.clear();
    this.prefetchQueue = [];

    super.destroy();

    if (window.neemans.topDeals?.[this.sectionId]) {
      delete window.neemans.topDeals[this.sectionId];
    }
  }

  /**
   * Arrow-key navigation between tabs (tablist semantics)
   * @private
   */
  #setupKeyboardNavigation() {
    if (!this.refs.collectionChips || this.refs.collectionChips.length === 0) return;

    this.element.addEventListener('keydown', event => {
      if (!event.target.matches('[ref="collectionChips[]"]')) return;

      const chips = this.refs.collectionChips;
      const currentIndex = chips.indexOf(event.target);
      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : chips.length - 1;
          chips[nextIndex].focus();
          this.selectChip(chips[nextIndex]);
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextIndex = currentIndex < chips.length - 1 ? currentIndex + 1 : 0;
          chips[nextIndex].focus();
          this.selectChip(chips[nextIndex]);
          break;
        case 'Home':
          event.preventDefault();
          chips[0].focus();
          this.selectChip(chips[0]);
          break;
        case 'End':
          event.preventDefault();
          chips[chips.length - 1].focus();
          this.selectChip(chips[chips.length - 1]);
          break;
      }
    });
  }

  /* ================================================================
     Viewport prefetch
     ================================================================ */

  /**
   * Start background prefetching when the section approaches the viewport.
   * Skipped entirely on save-data / constrained connections - the
   * click-to-fetch fallback keeps working regardless.
   * @private
   */
  #setupPrefetch() {
    if (!('IntersectionObserver' in window)) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      const saveData = connection.saveData === true;
      const constrained = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
      if (saveData || constrained) return;
    }

    this.prefetchObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.prefetchObserver.disconnect();
            this.prefetchObserver = null;
            this.#startPrefetch();
            break;
          }
        }
      },
      { rootMargin: PREFETCH_ROOT_MARGIN }
    );

    this.prefetchObserver.observe(this.element);
  }

  /**
   * Queue every not-yet-loaded collection for background prefetch
   * (runs once per section instance)
   * @private
   */
  #startPrefetch() {
    if (this.prefetchStarted) return;
    this.prefetchStarted = true;

    for (const chip of this.refs.collectionChips || []) {
      const key = this.#keyForChip(chip);
      const url = chip.dataset.collectionUrl;
      if (!key || !url) continue;
      if (this.collectionCache.has(key) || this.pendingRequests.has(key)) continue;

      this.prefetchQueue.push({ key, url });
    }

    this.#pumpPrefetchQueue();
  }

  /**
   * Drain the prefetch queue with bounded concurrency
   * @private
   */
  #pumpPrefetchQueue() {
    while (this.prefetching.size < PREFETCH_MAX_CONCURRENT && this.prefetchQueue.length > 0) {
      const { key, url } = this.prefetchQueue.shift();

      if (this.collectionCache.has(key) || this.pendingRequests.has(key)) {
        continue;
      }

      this.prefetching.add(key);
      this.#requestCollection(key, url, { userInitiated: false })
        .catch(() => {
          // Background prefetch failures are invisible - the collection
          // simply stays uncached and will fetch normally when clicked.
        })
        .finally(() => {
          this.prefetching.delete(key);
          this.#pumpPrefetchQueue();
        });
    }
  }

  /* ================================================================
     Tab interaction
     ================================================================ */

  // Public event handler - auto-bound via data-nms-click
  handleChipClick(event) {
    event.preventDefault();
    this.selectChip(event.currentTarget);
  }

  /**
   * Select a collection tab - serves from cache, reuses an in-flight
   * request, or fetches on demand via the Section Rendering API.
   * @param {HTMLElement} chip - Chip button element
   */
  selectChip(chip) {
    if (!chip || chip === this.activeChip) return;

    const url = chip.dataset.collectionUrl;
    if (!url) return;

    this.activeChip = chip;
    this.activeKey = this.#keyForChip(chip);
    this.#updateChipStates();

    // User interaction wins over background prefetch - take the key out
    // of the queue so a queue slot isn't spent on it later
    const queuedIndex = this.prefetchQueue.findIndex(entry => entry.key === this.activeKey);
    if (queuedIndex !== -1) {
      this.prefetchQueue.splice(queuedIndex, 1);
    }

    const cached = this.collectionCache.get(this.activeKey);
    if (cached) {
      this.#applyItems(cached);
      this.dispatchNMSEvent('deal-collection-changed', {
        sectionId: this.sectionId,
        collection: this.activeKey,
        source: 'cache',
      });
      return;
    }

    this.#loadForClick(this.activeKey, url);
  }

  /**
   * Shared request path for click-fetch and background prefetch.
   * Dedupes in-flight requests - a click on a prefetching collection
   * reuses the same promise instead of issuing a second request.
   * @param {string} key - Collection handle (cache key)
   * @param {string} url - Collection URL (from data-collection-url)
   * @param {object} [options]
   * @param {boolean} [options.userInitiated] - Abortable when superseded
   * @returns {Promise<Element[]>} Resolved parsed deal-item elements
   * @private
   */
  async #requestCollection(key, url, { userInitiated = true } = {}) {
    if (this.collectionCache.has(key)) {
      return this.collectionCache.get(key);
    }

    const existing = this.pendingRequests.get(key);
    if (existing) {
      return existing.promise;
    }

    const controller = new AbortController();

    const promise = (async () => {
      const response = await fetch(`${url}?section_id=${encodeURIComponent(this.itemsSection)}`, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      // DOMParser output is detached from the live document - parsed images
      // are not fetched and nothing enters the DOM until applyItems().
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('[data-deal-item]'));

      this.collectionCache.set(key, items);
      return items;
    })();

    const entry = { promise, controller, userInitiated };
    entry.promise.finally(() => {
      if (this.pendingRequests.get(key) === entry) {
        this.pendingRequests.delete(key);
      }
    });
    this.pendingRequests.set(key, entry);

    return promise;
  }

  /**
   * Load a collection in response to a user click and render it
   * @param {string} key - Collection handle
   * @param {string} url - Collection URL
   * @private
   */
  async #loadForClick(key, url) {
    // Abort the superseded request only if the user initiated it; a request
    // started by background prefetch is allowed to finish and populate cache.
    if (this.activeUserRequest && this.activeUserRequest.key !== key && this.activeUserRequest.userInitiated) {
      this.activeUserRequest.controller.abort();
    }

    const promise = this.#requestCollection(key, url, { userInitiated: true });
    const entry = this.pendingRequests.get(key);
    this.activeUserRequest = { key, controller: entry?.controller, userInitiated: entry?.userInitiated ?? true };

    this.#setLoading(true);

    try {
      const items = await promise;

      // Race guard: never apply a response for a collection that is no longer selected
      if (entry?.controller.signal.aborted || key !== this.activeKey) {
        return;
      }

      this.#applyItems(items);

      this.dispatchNMSEvent('deal-collection-changed', {
        sectionId: this.sectionId,
        collection: key,
        source: 'fetch',
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error(`NMS Top Deals: failed to load collection "${key}":`, error);

      // Keep UI consistent: revert selection to the collection still displayed.
      // The failed tab is not cached - clicking it again retries the request.
      if (key === this.activeKey) {
        this.activeChip = this.loadedChip;
        this.activeKey = this.#keyForChip(this.loadedChip);
        this.#updateChipStates();
      }
    } finally {
      // Only the latest user request clears the loading state
      if (this.activeUserRequest?.key === key) {
        this.#setLoading(false);
      }
    }
  }

  /**
   * Swap carousel items for the selected collection.
   * Children are replaced - the scroll-carousel element persists so carousel
   * controls (prev/next) and the scroll-progress component keep working.
   * @param {Element[]} items
   * @private
   */
  #applyItems(items) {
    if (!this.carousel) return;

    // Fetched tabs come back with the items section's full count — trim to this tab's limit
    const limit = Number(this.activeChip?.dataset.productsLimit) || items.length;
    this.carousel.replaceChildren(...items.slice(0, limit));
    this.loadedChip = this.activeChip;

    // "View all" follows the active tab (only in Automatic mode - a Manual
    // button gets no viewAllLink ref). A tab's own text / link win over
    // "<prefix> <collection>" and the collection URL.
    const chip = this.activeChip?.dataset;
    if (this.refs.viewAllLink && chip?.collectionUrl) {
      this.refs.viewAllLink.href = chip.viewAllUrl || chip.collectionUrl;
      if (this.refs.viewAllText) {
        const prefix = this.refs.viewAllLink.dataset.textPrefix || '';
        this.refs.viewAllText.textContent = chip.viewAllText || `${prefix} ${chip.collectionTitle || ''}`.trim();
      }
    }

    this.#toggleEmptyState(items.length === 0);
    this.#initDynamicContent();
    this.#refreshCarousel();
  }

  /**
   * Initialize NMS product cards in freshly inserted markup.
   * Custom elements (scroll-carousel children, product-form) self-initialize;
   * NMSProductCard instances require the shared init pass (idempotent via
   * data-initialized, so no duplicate listeners on re-inserted cached nodes).
   * @private
   */
  #initDynamicContent() {
    if (typeof window.initNMSProductCard === 'function') {
      window.initNMSProductCard();
    }
  }

  /**
   * Reset scroll position and refresh carousel-bound UI (prev/next disabled
   * states, is-scrollable flag, scroll progress) for the new item set.
   * @private
   */
  #refreshCarousel() {
    if (!this.carousel) return;

    window.requestAnimationFrame(() => {
      const itemCount = this.carousel.querySelectorAll('[data-deal-item]:not([hidden])').length;
      const isScrollable = this.carousel.scrollWidth - this.carousel.clientWidth > 1;

      this.carousel.classList.toggle('is-scrollable', isScrollable);

      // Back to the start of the newly selected collection
      if (itemCount > 0 && typeof this.carousel.select === 'function') {
        this.carousel.select(0, { instant: true });
      }
      this.carousel.scrollTo({ left: 0, behavior: 'auto' });

      // Prev is always disabled at the start; next depends on overflow.
      // (Synthetic scroll also fires, but no scroll event is emitted when the
      // position is already 0, so set the states deterministically.)
      const prevButton = this.element.querySelector('carousel-prev-button')?.firstElementChild;
      const nextButton = this.element.querySelector('carousel-next-button')?.firstElementChild;
      if (prevButton) prevButton.disabled = true;
      if (nextButton) nextButton.disabled = !isScrollable;

      // Notify carousel listeners and update the progress indicator
      this.carousel.dispatchEvent(new Event('scroll', { bubbles: true }));

      const progress = window.neemans?.carouselScrollProgress?.[this.progressId];
      if (progress && typeof progress.updateProgress === 'function') {
        progress.updateProgress();
      }
    });
  }

  /**
   * Toggle the loading state over the product area (layout stays stable -
   * previous products remain visible and tabs stay interactive).
   * Only shown for genuine cache misses - background prefetch is invisible.
   * @param {boolean} isLoading
   * @private
   */
  #setLoading(isLoading) {
    if (this.refs.loadingState) {
      this.refs.loadingState.hidden = !isLoading;
    }
    if (this.carousel) {
      this.carousel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.carousel.classList.toggle('nms-top-deals__track--loading', isLoading);
    }
  }

  #keyForChip(chip) {
    if (!chip) return null;
    const collection = chip.dataset.collectionHandle || chip.dataset.collectionUrl;
    // Limit is part of the key: two tabs on one collection can show different counts
    return collection ? `${collection}|${chip.dataset.productsLimit || ''}` : null;
  }

  #updateChipStates() {
    if (!this.refs.collectionChips) return;

    for (const chip of this.refs.collectionChips) {
      const isActive = chip === this.activeChip;

      chip.classList.toggle('nms-top-deals__chip--active', isActive);
      chip.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  #toggleEmptyState(isEmpty) {
    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = !isEmpty;
    }
    if (this.carousel) {
      this.carousel.classList.toggle('nms-top-deals__track--empty', isEmpty);
    }
  }
}

// Standard component initialization pattern
function initNMSTopDeals() {
  if (!window.NMSBaseClass) {
    console.error('NMS Top Deals: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-top-deals]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSTopDeals(element);
        instance.init();
        element.setAttribute('data-initialized', 'true');
      } catch (error) {
        console.error('Failed to initialize NMS Top Deals:', error, element);
      }
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSTopDeals);
} else {
  initNMSTopDeals();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSTopDeals);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initNMSTopDeals);
}

// Export for debugging and manual initialization
window.initNMSTopDeals = initNMSTopDeals;
window.getNMSTopDeals = function (sectionId) {
  if (sectionId) {
    return window.neemans?.topDeals?.[sectionId] || null;
  }
  return window.neemans?.topDeals || {};
};
